import {
  ELVA_CHAT_CLOSE_EVENT,
  ELVA_CHAT_OPEN_EVENT,
  ELVA_CHAT_OPEN_WITH_PAGE_DATA_EVENT,
  closeElvaChat,
  openElvaChat,
  type ElvaPageDataAttachment,
} from '../bridge/elvaChatBridge';
import { useAiAssistantStore } from '../store/aiAssistantStore';
import type { ElvaChatThemeInput } from '../theme/types';
import { useSyncElvaChatTheme } from '../theme/useSyncElvaChatTheme';
import type { ElvaFederatedRemoteModule } from '../types/loadRemote';
import type { ElvaChatController } from '../types/elvaChatController';
import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './elvaFederatedChatHost.css';

const REMOTE_ENTRY_URL = 'https://chat.elva.ai/assets/remoteEntry.js';
const MAX_LOAD_ATTEMPTS = 4;
const RETRY_DELAY_MS = 3000;

type ElvaChatControllerWithState = ElvaChatController & {
  state?: 'closed' | 'open' | 'minimized' | 'expanded';
};

export interface ElvaFederatedChatHostProps {
  loadRemote: () => Promise<ElvaFederatedRemoteModule>;
  /** Backend location_id for location-scoped chat API paths (from host location switcher). */
  locationId: string;
  /** When set, federated chat UI tokens follow the host @elva/ui theme. */
  theme?: ElvaChatThemeInput | null;
}

function createElvaFederatedChatOverlay(
  loadRemote: () => Promise<ElvaFederatedRemoteModule>,
  attempt: number,
  locationId: string
) {
  return lazy(async () => {
    await logRemoteEntryPreflight(attempt);

    try {
      const remote = await loadRemote();

      function ElvaChatBridge() {
        const location = useLocation();
        const elvaChat = remote.useElvaChat();
        const elvaChatRef = useRef(elvaChat);
        elvaChatRef.current = elvaChat;

        const aiAssistantOpen = useAiAssistantStore((s) => s.isOpen);
        const openAiAssistant = useAiAssistantStore((s) => s.open);
        const closeAiAssistant = useAiAssistantStore((s) => s.close);

        useSyncAiAssistantOpen(aiAssistantOpen, elvaChatRef);
        useDefaultPageData(elvaChatRef, location.pathname, location.search);
        useRowOpenEvents(elvaChatRef);
        useBridgeOpenCloseEvents(openAiAssistant, closeAiAssistant);
        useSyncStoreWhenChatCloses(elvaChat, aiAssistantOpen, closeAiAssistant);

        return null;
      }

      function ElvaFederatedChatRuntime() {
        function ElvaChatLogoLauncher() {
          const isOpen = useAiAssistantStore((s) => s.isOpen);

          const onClick = () => {
            if (isOpen) closeElvaChat();
            else openElvaChat();
          };

          return (
            <button
              type="button"
              onClick={onClick}
              aria-label="AI Assistant"
              className="elva-host-chat-fab fixed bottom-6 right-6 z-[var(--z-fab)] flex h-14 w-14 items-center justify-center rounded-full border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-3)] transition-transform duration-[var(--dur-fast)] hover:scale-[1.02] active:scale-[0.98]"
            >
              <img
                src="/logo.svg"
                alt="Elva"
                className="h-8 w-8 shrink-0 select-none object-contain"
              />
            </button>
          );
        }

        return (
          <remote.ElvaFederatedChatProvider renderLauncher={false} locationId={locationId}>
            <ElvaChatBridge />
            {/* Mounts chat shell/portal; default FAB hidden via elvaFederatedChatHost.css */}
            <div className="elva-remote-chat-shell">
              <remote.ElvaChatLauncher />
            </div>
            <ElvaChatLogoLauncher />
          </remote.ElvaFederatedChatProvider>
        );
      }

      return { default: ElvaFederatedChatRuntime };
    } catch (error) {
      console.error('[Elva AI Federation] Remote module failed to load', {
        attempt,
        remoteEntryUrl: REMOTE_ENTRY_URL,
        error,
      });
      throw error;
    }
  });
}

async function logRemoteEntryPreflight(attempt: number) {
  try {
    const response = await fetch(`${REMOTE_ENTRY_URL}?preflight=${String(Date.now())}`, {
      cache: 'no-store',
      mode: 'cors',
    });

    console.info('[Elva AI Federation] Remote entry preflight completed', {
      attempt: String(attempt),
      status: response.status,
      ok: response.ok,
    });
  } catch (error) {
    console.warn('[Elva AI Federation] Remote entry preflight failed', {
      attempt,
      remoteEntryUrl: REMOTE_ENTRY_URL,
      error,
    });
  }
}

class ElvaChatErrorBoundary extends React.Component<
  { children: React.ReactNode; onError: () => void },
  { hasError: boolean }
> {
  override state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  override componentDidCatch(error: unknown) {
    console.warn('[Elva AI Federation] Chat failed to load or render', error);
    this.props.onError();
  }

  override render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

function useSyncAiAssistantOpen(
  aiAssistantOpen: boolean,
  elvaChatRef: React.RefObject<ElvaChatController>
) {
  const prevOpenRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (prevOpenRef.current === aiAssistantOpen) return;

    const isInitialMount = prevOpenRef.current === null;
    prevOpenRef.current = aiAssistantOpen;

    if (isInitialMount && !aiAssistantOpen) return;

    if (aiAssistantOpen) {
      elvaChatRef.current?.open();
    } else {
      elvaChatRef.current?.close();
    }
  }, [aiAssistantOpen, elvaChatRef]);
}

function useDefaultPageData(
  elvaChatRef: React.RefObject<ElvaChatController>,
  pathname: string,
  search: string
) {
  const routeKey = `${pathname}${search}`;
  const lastAttachedRouteRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastAttachedRouteRef.current === routeKey) return;
    lastAttachedRouteRef.current = routeKey;

    elvaChatRef.current?.attachPageData({
      title: document.title || 'Page data',
      pageUrl: window.location.href,
      data: { data: 'no data' },
    });
  }, [routeKey, elvaChatRef]);
}

function useRowOpenEvents(elvaChatRef: React.RefObject<ElvaChatController>) {
  useEffect(() => {
    const openWithPageData = (event: Event) => {
      const attachment = (event as CustomEvent<ElvaPageDataAttachment>).detail;
      elvaChatRef.current?.openWithPageData(attachment);
    };

    window.addEventListener(ELVA_CHAT_OPEN_WITH_PAGE_DATA_EVENT, openWithPageData);

    return () => {
      window.removeEventListener(ELVA_CHAT_OPEN_WITH_PAGE_DATA_EVENT, openWithPageData);
    };
  }, [elvaChatRef]);
}

function useSyncStoreWhenChatCloses(
  elvaChat: ElvaChatControllerWithState,
  aiAssistantOpen: boolean,
  closeAiAssistant: () => void
) {
  const prevChatStateRef = useRef<ElvaChatControllerWithState['state']>(undefined);

  useEffect(() => {
    const previous = prevChatStateRef.current;
    const current = elvaChat.state;
    prevChatStateRef.current = current;

    if (
      previous !== undefined &&
      previous !== 'closed' &&
      current === 'closed' &&
      aiAssistantOpen
    ) {
      closeAiAssistant();
    }
  }, [elvaChat.state, aiAssistantOpen, closeAiAssistant]);
}

function useBridgeOpenCloseEvents(
  openAiAssistant: () => void,
  closeAiAssistant: () => void
) {
  useEffect(() => {
    const onOpen = () => {
      openAiAssistant();
    };

    const onClose = () => {
      closeAiAssistant();
    };

    window.addEventListener(ELVA_CHAT_OPEN_EVENT, onOpen);
    window.addEventListener(ELVA_CHAT_CLOSE_EVENT, onClose);

    return () => {
      window.removeEventListener(ELVA_CHAT_OPEN_EVENT, onOpen);
      window.removeEventListener(ELVA_CHAT_CLOSE_EVENT, onClose);
    };
  }, [openAiAssistant, closeAiAssistant]);
}

export function ElvaFederatedChatHost({ loadRemote, locationId, theme }: ElvaFederatedChatHostProps) {
  useSyncElvaChatTheme(theme);
  const [attempt, setAttempt] = useState(1);
  const ElvaFederatedChatOverlay = useMemo(
    () => createElvaFederatedChatOverlay(loadRemote, attempt, locationId),
    [loadRemote, attempt, locationId]
  );

  const retry = () => {
    if (attempt >= MAX_LOAD_ATTEMPTS) return;

    window.setTimeout(() => {
      setAttempt((currentAttempt) => currentAttempt + 1);
    }, RETRY_DELAY_MS);
  };

  return (
    <ElvaChatErrorBoundary key={attempt} onError={retry}>
      <Suspense fallback={null}>
        <ElvaFederatedChatOverlay />
      </Suspense>
    </ElvaChatErrorBoundary>
  );
}
